import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '@ieCore/services/auth.service';
import { NavService } from '@ieCore/services/nav.service';
import { selectUser } from '@ieApp/store/user/user.selectors';
import { Store } from '@ngrx/store';
import { filter } from 'rxjs';
import {
  RETURN_URL_PARAM,
  safeReturnUrl,
} from '@ieCore/utils/return-url.utils';
import logger from '@ieShared/utils/logger.utils';

export const guestGuard: CanActivateFn = (route) => {
  const authService = inject(AuthService);
  const navService = inject(NavService);
  const router = inject(Router);
  const store = inject(Store);

  if (authService.isAuthenticated()) {
    const userType = authService.getUserTypeFromToken();
    const requested = route.queryParamMap.get(RETURN_URL_PARAM);

    store
      .select(selectUser)
      .pipe(filter((user) => user !== null))
      .subscribe((user) => {
        const returnUrl = safeReturnUrl(requested, userType);

        // Same history hygiene as `AuthService.login()`: when a returnUrl was in
        // play the current entry is `/login?returnUrl=…`, carrying a copy of the
        // attempted URL. Replace it rather than push on top of it.
        const navigate = (commands: unknown[]) =>
          requested
            ? router.navigate(commands, { replaceUrl: true })
            : router.navigate(commands);

        if (returnUrl) {
          router
            .navigateByUrl(returnUrl, { replaceUrl: true })
            .then(() => logger.log('Navigated to the requested page'));

          return;
        }

        const homeRoute = navService.getRouteByUserTypeAndPermission(
          userType,
          user?.permissions,
        );

        if (homeRoute === undefined) {
          navigate(navService.to.notFound()).then(() =>
            logger.log('No accessible route: navigated to not found'),
          );

          return;
        }

        navigate([homeRoute]).then(() =>
          logger.log(`Navigated to ${userType} dashboard`),
        );
      });

    return false;
  }

  return true;
};
